type UserIdentifier = {
  id?: string;
  userId?: string;
};

type SessionIdentifier = {
  id?: string | number;
  sessionId?: string | number;
  processingProgress?: number | null;
};

type AdminSessionIdentifier = SessionIdentifier & {
  recorderUserId?: string;
  userId?: string;
};

type CommunityGroupIdentifier = {
  id?: string;
  groupId?: string;
  teamId?: string;
  ownerUserId?: string;
  ownerId?: string;
};

const requireIdentifier = (
  primary: string | number | undefined,
  legacy: string | number | undefined,
  entity: "User" | "Session",
  legacyField: "userId" | "sessionId",
): string => {
  const identifier = primary || legacy;
  if (!identifier) {
    throw new Error(
      `${entity} response is missing both id and ${legacyField}`,
    );
  }
  return String(identifier);
};

export const normalizeUserIdentifier = <T extends UserIdentifier>(
  user: T,
): T & { id: string; userId: string } => {
  const id = requireIdentifier(user.id, user.userId, "User", "userId");
  return { ...user, id, userId: user.userId || id };
};

export const normalizeOptionalUserIdentifier = <T extends UserIdentifier>(
  user: T | null | undefined,
): (T & { id: string; userId: string }) | null =>
  user ? normalizeUserIdentifier(user) : null;

export const normalizeSessionIdentifier = <T extends SessionIdentifier>(
  session: T,
): T & { id: string; sessionId: string } => {
  const id = requireIdentifier(
    session.id,
    session.sessionId,
    "Session",
    "sessionId",
  );
  return {
    ...session,
    id,
    sessionId: session.sessionId ? String(session.sessionId) : id,
    processingProgress: session.processingProgress ?? 0,
  } as T & { id: string; sessionId: string };
};

export const normalizeAdminSessionIdentifiers = <
  T extends AdminSessionIdentifier,
>(
  session: T,
): T & {
  id: string;
  sessionId: string;
  recorderUserId: string;
  userId: string;
} => {
  const identifiedSession = normalizeSessionIdentifier(session);
  const recorderUserId = requireIdentifier(
    session.recorderUserId,
    session.userId,
    "User",
    "userId",
  );

  return {
    ...identifiedSession,
    id: identifiedSession.id,
    sessionId: identifiedSession.sessionId,
    recorderUserId,
    userId: session.userId || recorderUserId,
  } as T & {
    id: string;
    sessionId: string;
    recorderUserId: string;
    userId: string;
  };
};

export const normalizeCommunityGroupIdentifiers = <
  T extends CommunityGroupIdentifier,
>(
  group: T,
): T & { id: string; ownerUserId: string } => {
  const id = group.id || group.groupId || group.teamId;
  if (!id) {
    throw new Error("Group response is missing id, groupId, and teamId");
  }

  const ownerUserId = group.ownerUserId || group.ownerId;
  if (!ownerUserId) {
    throw new Error("Group response is missing ownerUserId and ownerId");
  }

  return { ...group, id, ownerUserId };
};
